import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import './../../App.css';
import './Employees.css';
import { CreateButton, CreateSearchBarWithPlaceHolder } from './../CommonObjects';
import { SetAuditTrail } from './../Reports/AuditTrailReportEmployeeSetup';
import { ViewEmployeeModal as ViewEmployee } from './ViewEmployee';
import axios from '../../api/apiClient';
import { token, domain } from './../Security';
import {
  MAINTENACE_VESSEL,
  MAINTENACE_EMPLOYEETYPE,
  MAINTENACE_DEPARTMENT,
  EmployeeTypeIdSelectWithAll,
  VesselIdSelectWithAll,
  DepartmentIdSelectWithAll,
  GeneralMessage,
  useGeneralMessage,
  SearchBar,
} from "../../common/APICommon";

// MODIFIED: Use the modular V2 Hooks and Service
import { useFilterManager } from '../../v2/hooks/useFilterManager';
import { useEmployees } from '../../v2/hooks/useEmployees';
import { employeeService } from '../../v2/services/employeeService';
import { DynamicFilterBar } from '../../v2/components/filters/DynamicFilterBar';

// icons
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SearchIcon from '@mui/icons-material/Search';
import DoneIcon from '@mui/icons-material/Done';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { SettingInit } from './EmployeesListFld/component/SettingInit';
import { UploadForm } from './EmployeesListFld/forms/UploadForm';
import { UploadForBulkUpdateForm } from './EmployeesListFld/forms/UploadForBulkUpdateForm';
import { UploadLeaveForm } from './EmployeesListFld/forms/UploadLeaveForm';
import { BiometricsList } from './EmployeesListFld/component/BiometricsList';
import { UnapprovedEmployeeModal } from './EmployeesListFld/forms/UnapprovedEmployeeModal';
import { LoadingSpinner } from '../../common/Loading';
import { CreateTable } from './EmployeesListFld/table/CreateTable';
import {
  fetchFullEmployeeDetails,
} from './EmployeesListFld/EmployeesListApi';
import {
  createAccountBulk,
  processAddressFile,
  processBulkUpdateFile,
  processLeaveFile,
  processUploadEmployeeData
} from './EmployeesListFld/services/EmployeesListUpload';
import { generateDownloadTemplate } from './EmployeesListFld/services/excel/download/generateDownloadTemplate';
import { generateDownloadForBulkUpdateTemplate } from './EmployeesListFld/services/excel/download/generateDownloadForBulkUpdateTemplate';
import { generateDownloadMasterlist } from './EmployeesListFld/services/excel/download/generateDownloadMasterList';
import { generateDownloadEmployeeInformation } from './EmployeesListFld/services/excel/download/generateDownloadEmployeeInformation';
import { generateDownloadLeaveTemplate } from './EmployeesListFld/services/excel/download/generateDownloadLeaveTemplate';
import { generateDownloadAddressTemplate } from './EmployeesListFld/services/excel/download/generateDownloadAddressTemplate';
import { FEATURES } from '../../config/features';
import { useHRCalendar } from '../../v2/hooks/useHRCalendar';
import { useEmployeeTransaction } from '../../v2/hooks/useEmployeeTransaction';
import { EmployeeTransactionHistoryModal } from '../../v2/components/modules/EmployeeTransactions/modal/components/EmployeeTransactionHistoryModal';
import { useMaintenanceSalaryCombinationV2 } from '../../v2/hooks/useMaintenanceSalaryCombinationV2';
import { useEnumsApi } from '../../v2/hooks/useEnumsApi';
import { UploadAddressForm } from './EmployeesListFld/forms/UploadAddressForm';


/** Code Management Guide
1. refs           (settingRef, buttonRef)
2. state
   - UI/loading   (loading, loadingRequest, loadingUploadRequest)
   - modals       (showUploadForm, showBiometricsListForm, ...)
   - data         (employees, filterData, viewedData, ...)
   - filters      (departmentFilter, employeeTypeFilter, vesselFilter)
   - misc         (selectedEmployee, activeRowIds, isSelectAll, ...)
3. custom hooks   (useGeneralMessage, useEmployeesListUpload)
4. derived        (useMemo → filters)
5. callbacks      (fetchDataBasicInfo via useCallback)
6. effects        (useEffect calling fetchDataBasicInfo, useEffect for unapproved, useEffect for setting icon)
7. handlers       (handleFilter, handleUpdate, handleFileUpload, upload functions, download functions, handleSetting)
8. return JSX
*/

function EmployeesList({ user, userAccess, employeePhoto, setEmployeePhoto, isHideSideMenu }) {

  // ─────────────────────────────────────────────
  // REFS
  // ─────────────────────────────────────────────
  const settingRef = useRef(null);
  const buttonRef = useRef(null);


  // ─────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────

  // · · · Loading States · · ·
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loadingUploadRequest, setLoadingUploadRequest] = useState(false);

  // · · · Modal / Visibility States · · ·
  const [showSetting, setShowSetting] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showUploadForBulkUpdateForm, setShowUploadForBulkUpdateForm] = useState(false);
  const [showUploadLeaveUpdateForm, setShowUploadLeaveUpdateForm] = useState(false);
  const [showBiometricsListForm, setShowBiometricsListForm] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [showOnce, setShowOnce] = useState(false);
  const [showUnapprovedEmployees, setShowUnapprovedEmployees] = useState(false);
  const [showViewEmployeeHistoryModal, setShowViewEmployeeHistoryModal] = useState(false);


  // · · · Data States · · ·
  const [viewedData, setViewedData] = useState([]);
  const [uploadEmployees_temp, setUploadEmployees_temp] = useState([])
  const [uploadEmployeeInformations_temp, setUploadEmployeeInformations_temp] = useState([])
  const [uploadEmployeeLeaveInformations_temp, setUploadEmployeeLeaveInformations_temp] = useState([])
  const [uploadEmployeeAddressInformations_temp, setUploadEmployeeAddressInformations_temp] = useState([])

  // · · · MODIFIED: Filter Manager Hook · · ·
  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    applyFilters,
    resetFilters,
    loadingEnums
  } = useFilterManager(FEATURES.EMPLOYEE_FILTERS);


  // · · · MODIFIED: Employee Hook (Manages Employees list, loading, search, and unapproved) · · ·
  const {
    employees,
    unapprovedEmployees,
    loading: loadingData,
    handleSearch,
    refresh
  } = useEmployees(apiFilterPayload, loadingEnums);



  // · · · Selection States · · ·
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [selectedToApprove, setSelectedToApprove] = useState([]);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [activeRowIds, setActiveRowIds] = useState([]); // Array to store active row IDs
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // ─────────────────────────────────────────────
  // CUSTOM HOOKS
  // ─────────────────────────────────────────────
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const { currentYearData } = useHRCalendar({ showGeneralMessage });

  const {
    selectedEmployeeHistoryData,
    loading: loadingEmployeeHistoryData,
    fetchEmployeeHistoryData,
    clearEmployeeHistoryData,
  } = useEmployeeTransaction({ user, userAccess, showGeneralMessage });


  const { fetchMSCData, allRules, allBenefitGroups, loading: loadingMSCData } = useMaintenanceSalaryCombinationV2({
    showGeneralMessage,
    user: user,
    isMainModule: false,
  });

  const { maintenanceTypes, loading: loadingEnumsApi } = useEnumsApi();

  useEffect(() => { fetchMSCData(); }, [fetchMSCData]);


  // . . . Options from hooks (for uploadings) . . .
  const options = {
    mscRules: allRules,
    mscBenefitGroups: allBenefitGroups,
    maintenanceTypes: maintenanceTypes,
  };


  // ─────────────────────────────────────────────
  // NEW FUNCTIONS
  // ─────────────────────────────────────────────
  const validateActiveCalendar = useCallback(() => {
    if (!currentYearData || currentYearData.length === 0) {
      showGeneralMessage("Operation blocked: No active year found. Please set an active year in HR Calendar first.", false);
      return false;
    }
    return true;
  }, [currentYearData, showGeneralMessage]);

  // ─────────────────────────────────────────────
  // CONSTANTS
  // ─────────────────────────────────────────────
  const apiUrlBulkApprove = domain + "/api/Employee/update-status/bulk";
  const apiUrlUploadBulk = `${domain}/api/Employee/upload-bulk`;
  const apiUrlBulkUpdateInfo = `${domain}/api/Employee/update-information/bulk`;
  const apiUrlBulkUpdateLeave = `${domain}/api/Employee/update-leave/bulk`;
  const apiUrlBulkUpdateAddress = `${domain}/api/Employee/helper/upload-bulk-address-with-api`;
  const header = ["ID", "Full Name", "Department", "Is Approved?", ""]
  const accessor = ["employeeId", "fullName", "employeeInformation.departmentDescription", "employeeInformation.employeeInformationStatus", "manage"]


  // ─────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (unapprovedEmployees.length > 0 && !showOnce) {
      setShowUnapprovedEmployees(true);
      setShowOnce(true)
    }
  }, [unapprovedEmployees, showOnce])

  useEffect(() => {
    var settingIcon = document.getElementById("setting-icon");

    if (showSetting) {
      settingIcon.setAttribute("data-testid", "KeyboardArrowUpIcon");
      settingIcon.children[0].setAttribute("d", `M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z`);
    } else {
      settingIcon.setAttribute("data-testid", "KeyboardArrowDownIcon");
      settingIcon.children[0].setAttribute("d", `M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z`);
    }

    const handleClickOutside = (event) => {
      if (settingRef.current && !settingRef.current.contains(event.target)) {
        if (buttonRef.current && !buttonRef.current.contains(event.target)) {
          setShowSetting(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [showSetting]);


  // ─────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────

  // · · · Approval Handlers · · ·
  const handleUpdate = async () => {
    if (selectedEmployee.length <= 0) {
      showGeneralMessage("Nothing to update.", false);
      return;
    }

    const data = selectedEmployee.map(_employee => {
      return {
        employeePersonalInformationId: _employee.id,
        status: "Approved"
      };
    })

    setLoadingRequest(true);
    try {
      // MODIFIED: Using the new Service for bulk approval
      await employeeService.bulkApprove(data);

      // MODIFIED: Call refresh from hook instead of manual fetch
      await refresh();

      showGeneralMessage("Update successful", true);
      setActiveRowIds([]);
      setSelectedEmployee([]);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  // · · · Modal / Navigation Handlers · · ·
  const addHandleClick = () => { window.location.pathname = "/addEmployee"; }
  const closeUnapprovedEmployees = () => { setShowUnapprovedEmployees(false); }
  const viewEmployeeHandleClick = useCallback(async (data) => {
    setLoadingRequest(true); // MODIFIED: Reuse request loader
    try {
      const fullEmployeeData = await fetchFullEmployeeDetails(data.id);
      setViewedData(fullEmployeeData);
      setShowViewEmployee(true);
    } catch (error) {
      console.error("Failed to load employee details", error);
    } finally {
      setLoadingRequest(false);
    }
  }, []);

  const closeViewEmployeeHandleClick = () => { setShowViewEmployee(false); }
  const viewEmployeeTransactionHistoryHandleClick = useCallback(async (data) => {
    setLoadingRequest(true);
    try {
      // MODIFIED: Capture name and fetch data
      setSelectedEmployeeName(data.fullName);
      await fetchEmployeeHistoryData(data.id);
      setShowViewEmployeeHistoryModal(true);
    } finally {
      setLoadingRequest(false);
    }
  }, [fetchEmployeeHistoryData]);

  const closeEmployeeTransactionHistory = () => {
    setShowViewEmployeeHistoryModal(false);
    setSelectedEmployeeName("");
    clearEmployeeHistoryData();
  }
  const openUploadFormHandleClick = () => {
    if (validateActiveCalendar()) {
      setShowUploadForm(true);
    }
  }
  const closeUploadFormHandleClick = () => { setShowUploadForm(false); }
  const openUploadForBulkUpdateEmployeeInformationFormHandleClick = () => { setShowUploadForBulkUpdateForm(true); }
  const closeUploadForBulkUpdateEmployeeInformationFormHandleClick = () => { setShowUploadForBulkUpdateForm(false); }
  const openBiometricsListFormHandleClick = () => { setShowBiometricsListForm(true); }
  const closeBiometricsListFormHandleClick = () => { setShowBiometricsListForm(false); }
  const openUploadLeaveFormHandleClick = () => {
    if (validateActiveCalendar()) {
      setShowUploadLeaveUpdateForm(true);
    }
  }
  const closeUploadLeaveFormHandleClick = () => { setShowUploadLeaveUpdateForm(false); }

  const [showUploadAddressUpdateForm, setShowUploadAddressUpdateForm] = useState(false);
  const openUploadAddressFormHandleClick = () => {
    if (validateActiveCalendar()) {
      setShowUploadAddressUpdateForm(true);
    }
  }
  const closeUploadAddressFormHandleClick = () => { setShowUploadAddressUpdateForm(false); }

  const toggleSetting = () => { setShowSetting(!showSetting); };

  // · · · Upload Handlers · · ·
  // Upload Employee Data Bulk
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setLoadingUploadRequest(true);
    try {
      const processedData = await processUploadEmployeeData({ file: files[0], employees, options });
      setUploadEmployees_temp(processedData);
    } catch (error) {
      showGeneralMessage("Failed to process file.", false);
    } finally {
      setLoadingUploadRequest(false);
    }
  }

  // Upload Employee Data Bulk
  const handleFinalFileUpload = async (bulkData) => {
    setLoadingRequest(true);
    try {
      await axios.post(apiUrlUploadBulk, bulkData, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      await createAccountBulk(bulkData, showGeneralMessage);
      await SetAuditTrail({ setupArea: "Employee List", actionTaken: "insert", field: "Upload Employee/s", newValue: "-", oldValue: "-", dateSaved: new Date(), savedById: user.id });
      await refresh(); // MODIFIED
      showGeneralMessage('Upload successful', true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  }
  const handleFileUploadForBulkUpdate = async (files) => {
    if (!files || files.length === 0) return;
    setLoadingUploadRequest(true);
    try {
      const processedData = await processBulkUpdateFile({ file: files[0], employees, options });
      setUploadEmployeeInformations_temp(processedData);
    } catch (error) {
      showGeneralMessage("Failed to process file.", false);
    } finally {
      setLoadingUploadRequest(false);
    }
  }
  const handleFinalFileUploadForBulkUpdate = async (bulkData) => {
    setLoadingRequest(true);
    try {
      console.log({bulkData});

      await axios.put(apiUrlBulkUpdateInfo, bulkData, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      await SetAuditTrail({ setupArea: "Employee List", actionTaken: "upload", field: "Upload For Bulk Update Employee Information", newValue: "-", oldValue: "-", dateSaved: new Date(), savedById: user.id });
      await refresh(); // MODIFIED
      showGeneralMessage('Upload successful', true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  }
  const handleFileUploadLeave = async (files) => {
    if (!files || files.length === 0) return;
    setLoadingUploadRequest(true);
    try {
      const processedData = await processLeaveFile(files[0], employees);
      setUploadEmployeeLeaveInformations_temp(processedData);
    } catch (error) {
      showGeneralMessage("Failed to process file.", false);
    } finally {
      setLoadingUploadRequest(false);
    }
  }
  const handleFinalFileUploadLeave = async (bulkData) => {
    setLoadingRequest(true);
    try {
      await axios.put(apiUrlBulkUpdateLeave, bulkData, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      await SetAuditTrail({ setupArea: "Employee List", actionTaken: "upload", field: "Upload Employee's Leave", newValue: "-", oldValue: "-", dateSaved: new Date(), savedById: user.id });
      await refresh(); // MODIFIED
      showGeneralMessage('Upload successful', true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  }

  const handleFileUploadAddress = async (files) => {
    if (!files || files.length === 0) return;

    setLoadingUploadRequest(true);
    try {
      const processedData = await processAddressFile(files[0], employees);
      console.log({processedData})
      setUploadEmployeeAddressInformations_temp(processedData);
    } catch (error) {
      showGeneralMessage("Failed to process file.", false);
    } finally {
      setLoadingUploadRequest(false);
    }
  }
  const handleFinalFileUploadAddress = async (bulkData) => {
    setLoadingRequest(true);
    try {
      await axios.post(apiUrlBulkUpdateAddress, bulkData, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      await SetAuditTrail({ setupArea: "Employee List", actionTaken: "upload", field: "Upload Employee's Address", newValue: "-", oldValue: "-", dateSaved: new Date(), savedById: user.id });
      await refresh(); // MODIFIED
      showGeneralMessage('Upload successful', true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  }

  // · · · Download Handlers · · ·
  const handleDownloadEmployeeInformation = useCallback(async () => {
    setLoadingRequest(true);
    try {
      // MODIFIED: Changed activeFilterState to apiFilterPayload
      await generateDownloadEmployeeInformation(apiFilterPayload, showGeneralMessage);
    } catch (error) {
      showGeneralMessage("An error occurred during download", false);
    } finally {
      setLoadingRequest(false);
    }
  }, [apiFilterPayload, showGeneralMessage]); // MODIFIED: dependency

  const handleDownloadMasterlist = useCallback(async () => {
    setLoadingRequest(true);
    try {
      // MODIFIED: Changed activeFilterState to apiFilterPayload
      await generateDownloadMasterlist(apiFilterPayload, showGeneralMessage);
    } catch (error) {
      showGeneralMessage("An error occurred during download", false);
      console.error({ error })
    } finally {
      setLoadingRequest(false);
    }
  }, [apiFilterPayload, showGeneralMessage])


  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="EmployeesTabInfo shadow-xl">
            <div className="employeesTab-header">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Personnel >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Employees List"}</span>
              </div>
              <div className='row' style={{ justifyContent: "space-between", alignItems: "center" }}>
                <h3>Employees List</h3>
                <div style={{ width: "30%", textAlign: "right" }}>
                  <button ref={buttonRef} onClick={toggleSetting} title="Setting" type="button" className="setting">
                    <SettingsIcon />
                    <KeyboardArrowDownIcon id="setting-icon" />
                  </button>
                  <div ref={settingRef}>
                    {showSetting &&
                      <SettingInit
                        generateDownloadTemplate={generateDownloadTemplate}
                        generateDownloadMasterlist={handleDownloadMasterlist}
                        generateDownloadForBulkUpdateTemplate={generateDownloadForBulkUpdateTemplate}
                        generateDownloadEmployeeInformation={handleDownloadEmployeeInformation}
                        openUploadFormHandleClick={openUploadFormHandleClick}
                        openUploadForBulkUpdateEmployeeInformationFormHandleClick={openUploadForBulkUpdateEmployeeInformationFormHandleClick}
                        openBiometricsListFormHandleClick={openBiometricsListFormHandleClick}
                        generateDownloadLeaveTemplate={generateDownloadLeaveTemplate}
                        openUploadLeaveFormHandleClick={openUploadLeaveFormHandleClick}

                        generateDownloadAddressTemplate={generateDownloadAddressTemplate}
                        openUploadAddressFormHandleClick={openUploadAddressFormHandleClick}
                      />}
                  </div>
                </div>
              </div>
            </div>

            <div className="employeesTab-body">
              <div className="bg-white rounded-lg border-b border-gray-100 mt-2 p-4" style={{ border: "1px solid #d7d7d7" }}>
                <div className="border-b border-gray-50 mb-4">
                  <span className="text-lg font-semibold text-gray-600">Filters</span>
                </div>
                {/* MODIFIED: Using Filter Manager props */}
                <DynamicFilterBar
                  requestedFilters={FEATURES.EMPLOYEE_FILTERS || []}
                  activeFilterState={activeFilterState}
                  onFilterChange={onFilterChange}
                  onApply={applyFilters}
                  onClear={resetFilters}
                />
              </div>

              <div className="body-top employee-body-top row">
                <div className="left w-1/2">
                  <SearchBar handleFilter={(val) => handleSearch(val)} />
                </div>
                {/* MODIFIED: Using handleSearch from useEmployees hook */}
                {/* <div className="body-top-search">
                  {CreateSearchBarWithPlaceHolder("", "mainte-search row", "mainte-search-icon", "mainte-search-input", (e) => handleSearch(e.target.value), "Search ... ", SearchIcon)}
                </div> */}
              </div>
              <div className="body-top-btn row">
                {CreateButton("", "add row", addHandleClick, "ADD EMPLOYEE", PeopleAltIcon)}
                {CreateButton("", "approve row", handleUpdate, "APPROVE", DoneIcon)}
                {CreateButton("", "approve row", () => setIsSelectAll(true), "SELECT ALL", ChecklistIcon)}
              </div>
              <div className='employeesTab-tab'>
                <div id="container1" className="employeesTab-table container">
                  <CreateTable
                    dataList={employees}
                    dataHeader={header}
                    dataAccessor={accessor}
                    editHandleClick={viewEmployeeHandleClick}
                    viewEmployeeTransactionHistoryHandleClick={viewEmployeeTransactionHistoryHandleClick}
                    setSelectedEmployee={setSelectedEmployee}
                    employees={employees}
                    selectedToApprove={selectedToApprove}
                    setSelectedToApprove={setSelectedToApprove}
                    activeRowIds={activeRowIds}
                    setActiveRowIds={setActiveRowIds}
                    isSelectAll={isSelectAll}
                    setIsSelectAll={setIsSelectAll}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODIFIED: Using refresh from hook for modal actions */}
      <UploadForm isOpen={showUploadForm} onClose={closeUploadFormHandleClick} uploadEmployees_temp={uploadEmployees_temp} setUploadEmployees_temp={setUploadEmployees_temp} handleFileUpload={handleFileUpload} handleFinalFileUpload={handleFinalFileUpload} />
      <UploadForBulkUpdateForm isOpen={showUploadForBulkUpdateForm} onClose={closeUploadForBulkUpdateEmployeeInformationFormHandleClick} uploadEmployeeInformations_temp={uploadEmployeeInformations_temp} setUploadEmployeeInformations_temp={setUploadEmployeeInformations_temp} handleFileUpload={handleFileUploadForBulkUpdate} handleFinalFileUploadForBulkUpdate={handleFinalFileUploadForBulkUpdate} />
      <UploadLeaveForm isOpen={showUploadLeaveUpdateForm} onClose={closeUploadLeaveFormHandleClick} uploadEmployeeLeaveInformations_temp={uploadEmployeeLeaveInformations_temp} setUploadEmployeeLeaveInformations_temp={setUploadEmployeeLeaveInformations_temp} handleFileUpload={handleFileUploadLeave} handleFinalFileUploadLeave={handleFinalFileUploadLeave} />
      <UploadAddressForm 
        isOpen={showUploadAddressUpdateForm} 
        onClose={closeUploadAddressFormHandleClick} 
        tempData={uploadEmployeeAddressInformations_temp} 
        setTempData={setUploadEmployeeAddressInformations_temp} 
        handleFileUpload={handleFileUploadAddress} 
        handleFinalFileUpload={handleFinalFileUploadAddress} 
      />

      {showBiometricsListForm && <BiometricsList isOpen={showBiometricsListForm} onClose={closeBiometricsListFormHandleClick} employees={employees} fetchDataBasicInfo={refresh} user={user} />}
      {showViewEmployee && <ViewEmployee user={user} isOpen={showViewEmployee} onClose={closeViewEmployeeHandleClick} viewedData={viewedData} setViewedData={setViewedData} fetchData={refresh} employeePhoto={employeePhoto} setEmployeePhoto={setEmployeePhoto} />}

      {showViewEmployeeHistoryModal && (
        <EmployeeTransactionHistoryModal
          isOpen={showViewEmployeeHistoryModal}
          onClose={() => closeEmployeeTransactionHistory()}
          data={selectedEmployeeHistoryData}
          employeeName={selectedEmployeeName}
        />
      )}

      <UnapprovedEmployeeModal isOpen={showUnapprovedEmployees} onClose={closeUnapprovedEmployees} employees={unapprovedEmployees} />
      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />

      {/* MODIFIED: Combined all loading states */}
      <LoadingSpinner loading={loadingData || loadingRequest || loadingUploadRequest || loadingEnums || loadingMSCData || loadingEnumsApi} />
    </div>
  )
}

export default EmployeesList